import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, makeStyles } from "@material-ui/core";
import MouseIcon from "@material-ui/icons/Mouse";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import StopIcon from "@material-ui/icons/Stop";
import RoomContext from "../../context/RoomContext";
import { makePeerConsumerSelector } from "../../helpers/Selectors";
import * as roomActions from "../../actions/roomActions";

const useStyles = makeStyles(() => ({
  bar: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 12,
    background: "rgba(15, 23, 42, 0.88)",
    border: "1px solid rgba(148, 163, 184, 0.35)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.28)",
    pointerEvents: "auto",
  },
  miniBar: {
    gap: 4,
    padding: "4px 6px",
    borderRadius: 8,
  },
  btn: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.2,
    borderRadius: 8,
    padding: "6px 12px",
    minHeight: 32,
    color: "#fff",
    borderColor: "rgba(255,255,255,0.28)",
    background: "rgba(255,255,255,0.08)",
    "&:hover": {
      background: "rgba(255,255,255,0.16)",
      borderColor: "rgba(255,255,255,0.4)",
    },
    "& .MuiButton-startIcon": {
      marginRight: 6,
    },
  },
  miniBtn: {
    fontSize: 10,
    padding: "4px 8px",
    minHeight: 26,
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
  },
  requestBtn: {
    background: "#006bd6",
    borderColor: "#006bd6",
    color: "#fff",
    "&:hover": {
      background: "#0057b3",
      borderColor: "#0057b3",
    },
  },
  stopBtn: {
    background: "rgba(227, 59, 74, 0.22)",
    borderColor: "rgba(248, 113, 113, 0.55)",
    color: "#fecaca",
    "&:hover": {
      background: "rgba(227, 59, 74, 0.38)",
      borderColor: "#f87171",
    },
  },
  waitingLabel: {
    color: "rgba(255,255,255,0.92)",
    textTransform: "none",
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 8px",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  miniWaitingLabel: {
    fontSize: 10,
  },
}));

const RemoteControlScreenActions = ({ hostPeerId }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { roomClient } = useContext(RoomContext);
  const meId = useSelector((state) => state.me.id);
  const isMini = useSelector((state) => state.call.displayMini);
  const fullScreenConsumerId = useSelector(
    (state) => state.room.fullScreenConsumer
  );
  const session = useSelector((state) => state.remoteControl) || {
    status: "idle",
  };
  const host = useSelector((state) => state.peers[hostPeerId]);
  const getPeerConsumers = makePeerConsumerSelector();
  const { screenConsumer } = useSelector((state) =>
    getPeerConsumers(state, hostPeerId)
  );
  const canRequest = useSelector((state) => {
    const roles = state.me.roles || [];
    const perms = state.room.permissionsFromRoles || {};
    const allowed =
      perms.REQUEST_REMOTE_CONTROL && perms.REQUEST_REMOTE_CONTROL.length
        ? perms.REQUEST_REMOTE_CONTROL
        : perms.SHARE_SCREEN || [];

    return roles.some((role) => allowed.includes(role));
  });

  if (!hostPeerId || hostPeerId === meId) return null;

  const controllable = Boolean(
    host &&
      host.clientInfo &&
      host.clientInfo.capabilities &&
      host.clientInfo.capabilities.canBeControlled
  );

  const isController = session.controllerPeerId === meId;
  const isThisHost = session.hostPeerId === hostPeerId;
  const screenVisible =
    Boolean(screenConsumer) &&
    !screenConsumer.locallyPaused &&
    !screenConsumer.remotelyPaused;
  const isFullscreen =
    Boolean(screenConsumer) && fullScreenConsumerId === screenConsumer.id;
  const isActiveController =
    controllable &&
    session.status === "active" &&
    isThisHost &&
    isController;
  const showWaiting =
    controllable &&
    session.status === "requested" &&
    isThisHost &&
    isController;
  const showRequest =
    controllable && canRequest && session.status === "idle";
  const showFullscreen = Boolean(screenConsumer) && screenVisible;

  if (!showWaiting && !showRequest && !isActiveController && !showFullscreen) {
    return null;
  }

  const barClass = `${classes.bar} ${isMini ? classes.miniBar : ""}`;
  const btnClass = `${classes.btn} ${isMini ? classes.miniBtn : ""}`;

  const toggleFullscreen = () => {
    if (screenConsumer) {
      dispatch(roomActions.toggleConsumerFullscreen(screenConsumer.id));
    }
  };

  return (
    <div className={barClass}>
      {showWaiting && (
        <span
          className={`${classes.waitingLabel} ${
            isMini ? classes.miniWaitingLabel : ""
          }`}
        >
          <FormattedMessage
            id="remoteControl.waiting"
            defaultMessage="Waiting for approval"
          />
        </span>
      )}

      {showRequest && !showWaiting && (
        <Button
          variant="outlined"
          size="small"
          className={`${btnClass} ${classes.requestBtn}`}
          startIcon={<MouseIcon fontSize="small" />}
          onClick={() => roomClient.requestRemoteControl(hostPeerId)}
        >
          {intl.formatMessage({
            id: "remoteControl.request",
            defaultMessage: "Request control",
          })}
        </Button>
      )}

      {showFullscreen && (
        <Button
          variant="outlined"
          size="small"
          className={btnClass}
          startIcon={
            isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )
          }
          onClick={toggleFullscreen}
        >
          {intl.formatMessage({
            id: isFullscreen
              ? "label.fullscreenExit"
              : "label.fullscreenExpand",
            defaultMessage: isFullscreen ? "Exit fullscreen" : "Enlarge",
          })}
        </Button>
      )}

      {isActiveController && (
        <Button
          variant="outlined"
          size="small"
          className={`${btnClass} ${classes.stopBtn}`}
          startIcon={<StopIcon fontSize="small" />}
          onClick={() => roomClient.endRemoteControl()}
        >
          {intl.formatMessage({
            id: "remoteControl.stop",
            defaultMessage: "Stop control",
          })}
        </Button>
      )}
    </div>
  );
};

export default RemoteControlScreenActions;
